import { CommonPageWrapper, HeadingWrapper } from 'admin/components/wrappers';
import AdminCustomButton from 'app/components/AdminCustomButton';
import BackButton from 'app/components/BackButton';
import CustomBreadCrumb from 'app/components/CustomBreadCrumb';
import { PageMainLabel } from 'app/components/SHLabel';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { createBreadCrumpitems, sponsorDefaultValue } from './staticDatas';
import { useNavigateToRoute } from 'utils/common';
import { GeneralLookup, Sponsor } from 'services/openapi';
import SponsorForm from './Components/SponsorForm';
import SimpleReactValidator from 'simple-react-validator';
import useSponserApi from 'admin/hooks/useSponserApi';
import {
    FileData,
    generateUniqueId,
} from 'app/components/MultipleFileUploadCard';
import { useParams } from 'react-router-dom';

type Props = {};

const SponserCreateViewPage = (props: Props) => {
    const { id, action } = useParams();
    const redirect = useNavigateToRoute();
    const { handleCreateSponsor, viewSponser, getSponser, updateSponsor } =
        useSponserApi();
    const simpleValidator = useRef(new SimpleReactValidator());
    const [valid, setValid] = useState<boolean>(false);
    const [selectedFiles, setSelectedFiles] = useState<FileData[]>([]);
    const [deletedImageIds, setDeletedImageIds] = useState<string[]>([]);
    const [sponserPayload, setSponserPayload] =
        React.useState<Sponsor>(sponsorDefaultValue);
    const [categoryDeafultvalue, setCategoryDeafultvalue] =
        React.useState<GeneralLookup>();
    const [triggor, setTriggor] = React.useState(false);
    const handleBackClick = () => {
        redirect(`/admin/sponsors`);
    };
    const onCancelClick = () => {
        if (id) {
            if (viewSponser) {
                setCategoryDeafultvalue(viewSponser?.divisionClass?.category);
                let divisionClassView: GeneralLookup = {
                    id: viewSponser?.divisionClass?.id as string,
                    name: viewSponser?.divisionClass?.name as string,
                };
                setSponserPayload({
                    ...sponserPayload,
                    name: viewSponser?.sponsorName,
                    divisionClass: divisionClassView,
                });
            }
            if (viewSponser?.attachments?.length) {
                // Create FileData objects from attachments
                const attachmentFiles = viewSponser.attachments.map(attachment => {
                    const fileName = attachment?.url?.split('\\').pop() || 'file';
                    // Determine file type based on extension
                    const fileExtension = fileName.split('.').pop()?.toLowerCase();
                    let fileType = 'image/*'; // Default to image
                    const fileSize = attachment?.size! * 1024; // We don't know size from URL
                    // Set appropriate file type based on extension
                    if (fileExtension === 'pdf') {
                        fileType = 'application/pdf';
                    } else if (['doc', 'docx'].includes(fileExtension || '')) {
                        fileType = 'application/msword';
                    } else if (['xls', 'xlsx'].includes(fileExtension || '')) {
                        fileType = 'application/vnd.ms-excel';
                    }

                    return {
                        id: attachment.id,
                        url: attachment.url,
                        previewUrl: attachment.url,
                        name: fileName,
                        size: fileSize, // We don't know size from URL
                        type: fileType,
                        uniqueId: generateUniqueId(),
                    } as FileData;
                });

                setSelectedFiles(attachmentFiles);
            } else {
                setSelectedFiles([]);
            }
        } else {
            setSponserPayload(sponsorDefaultValue);
            setCategoryDeafultvalue(undefined);
            simpleValidator.current.hideMessages();
            simpleValidator.current.visibleFields = [];
            setSelectedFiles([]);
        }
    };
    const handleFileDelete = (fileData: FileData, index: number) => {
        // If the file has an ID (meaning it's from the backend), track it for deletion
        if (fileData.id) {
            setDeletedImageIds(prev => [...prev, fileData.id as string]);
        }

        // Remove from selected files
        const newSelectedFiles = [...selectedFiles];
        newSelectedFiles.splice(index, 1);
        setSelectedFiles(newSelectedFiles);
    };
    const onSaveClick = () => {
        let isFormValid = simpleValidator.current.allValid();
        if (isFormValid || valid) {
            if (id) {
                updateSponsor(
                    { ...sponserPayload, id: id },
                    selectedFiles,
                    deletedImageIds,
                );
            } else {
                handleCreateSponsor(sponserPayload, selectedFiles);
            }
        } else {
            simpleValidator.current.showMessages();
            setTriggor(!triggor);
        }
    };
    React.useEffect(() => {
        const formValid = simpleValidator.current.allValid();
        setValid(formValid);
    }, [sponserPayload]);
    React.useEffect(() => {
        if (id) {
            getSponser(id);
        }
    }, [id]);
    return (
        <>
            <CommonPageWrapper>
                <HeadingWrapper>
                    <PageMainLabel>
                        {action === 'edit' ? 'Edit Sponsor' : 'Add Sponsor'}
                    </PageMainLabel>
                    <CustomBreadCrumb preData={createBreadCrumpitems} />
                </HeadingWrapper>
                <BtnWrapper>
                    <div>
                        <BackButton onClick={handleBackClick} />
                    </div>
                    <div className="btn-action">
                        <AdminCustomButton buttonType="cancel" onClick={onCancelClick}>
                            Clear
                        </AdminCustomButton>
                        <AdminCustomButton buttonType="save" onClick={onSaveClick}>
                            Save
                        </AdminCustomButton>
                    </div>
                </BtnWrapper>
                <SponsorForm
                    setSponserPayload={setSponserPayload}
                    sponserPayload={sponserPayload}
                    simpleValidator={simpleValidator}
                    setCategoryDeafultvalue={setCategoryDeafultvalue}
                    categoryDeafultvalue={categoryDeafultvalue}
                    setSelectedFiles={setSelectedFiles}
                    selectedFiles={selectedFiles}
                    viewSponser={viewSponser}
                    onFileDelete={handleFileDelete}
                />
            </CommonPageWrapper>
        </>
    );
};

export default SponserCreateViewPage;
const BtnWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  padding: 30px 0;
  @media (min-width: 1480px) and (max-width: 1899px) {
    padding: 15px 20px 15px 0;
  }
  @media (min-width: 1899px) and (max-width: 4000px) {
    padding: 15px 35px 15px 0;
  }
  .btn-action {
    display: flex;
    align-items: center;
    gap: 15px;
  }
`;
